import React from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { HeroSection } from "./../components/HeroSection";
import CivicPointsIconLogo from "../../../assets/icons/civicpoints_logo.svg";
import LoginForm from "./components/LoginForm";
import { useLogin } from "./hooks/useLogin";
import type { LoginFormValues } from "./types/login.types";
import { TERMS_URL } from "../../../config/runtime";
import { useAuthSession } from "../shared";
import { getMerchantDashboardRedirect } from "../merchant-register/utils/merchantOnboarding";

interface LoginPageProps {
  onLoginSuccess?: () => void;
  darkMode?: boolean;
  onToggleDarkMode?: () => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoggedIn, merchantOnboardingStage, user } = useAuthSession();

  const { submitLogin, loading, error, form } = useLogin();

  if (isLoggedIn && user) {
    const merchantRedirect = getMerchantDashboardRedirect(user.role, merchantOnboardingStage);
    return (
      <Navigate
        to={merchantRedirect ?? (user.role === "merchant" ? "/merchant/home" : "/dashboard")}
        replace
      />
    );
  }

  const onSubmit = async (values: LoginFormValues) => {
    try {
      await submitLogin(values.email, values.password, onLoginSuccess);
    } catch (err) {
      console.error("Login error:", err);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-white dark:bg-[#1a1a1a]">
      <div className="lg:hidden flex items-center justify-center py-8 bg-[#eef9ef] dark:bg-[#0d2721]">
        <img
          src={CivicPointsIconLogo}
          alt="CivicPoints"
          className="h-12 w-auto"
        />
      </div>

      <HeroSection />

      <div className="flex-1 lg:w-[50%] flex flex-col p-6 md:p-8 lg:p-12 bg-white dark:bg-[#242424]">
        <div className="flex-1 flex flex-col">
          <div className="flex-1 flex items-center justify-center">
            <div className="w-full max-w-[568px]">
              <div className="hidden lg:flex justify-center mb-8">
                <img
                  src={CivicPointsIconLogo}
                  alt="CivicPoints"
                  className="h-16 w-auto"
                />
              </div>

              <LoginForm
                t={t}
                loading={loading}
                error={error}
                form={form}
                onSubmit={onSubmit}
                onMerchantRegister={() => navigate("/merchant/register/account")}
              />
            </div>
          </div>

          <div className="text-center mt-8 pb-4">
            <div className="flex items-center justify-center gap-2 text-sm text-[#75757d] dark:text-[#a0a0a0]">
              <a
                href={TERMS_URL}
                className="hover:text-[#00c49a] transition-colors"
              >
                Terms of use
              </a>
              <span>|</span>
              <a
                href="https://civicpoints.io/privacy-policy"
                className="hover:text-[#00c49a] transition-colors"
              >
                Privacy policy
              </a>
              <span>|</span>
              <a
                href="https://helpdesk.civicpoints.io/en/customer/create-ticket/"
                className="hover:text-[#00c49a] transition-colors"
              >
                Help Center
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
